import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  browserSessionResetEvent,
  endRejectedBrowserSession,
  isRefreshSessionRejected,
  markBrowserSessionUnavailable,
  noteExternalBrowserSessionMutation,
  refreshAuthSession,
  restoreBrowserSession,
  SESSION_UNAVAILABLE_MESSAGE,
} from '@/api/authSession';
import { subscribeBrowserSessionMutations } from '@/api/browserSessionEvents';
import { Button, Modal } from '@/components/ui';
import { BrowserSessionLockUnavailableError } from '@/api/browserSessionLock';
import { useAuthStore } from '@/stores/authStore';
import { readAccessTokenExpiry } from '@/utils/accessTokenExpiry';
import { config } from '@/config/env';

/** The blocking session screen can't be dismissed; the person retries or waits. */
const ignoreDismiss = () => undefined;

export default function AuthSessionManager() {
  const queryClient = useQueryClient();
  const token = useAuthStore((state) => state.token);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const sessionStatus = useAuthStore((state) => state.sessionStatus);
  const sessionCheckError = useAuthStore((state) => state.sessionCheckError);
  const setSessionCheckError = useAuthStore(
    (state) => state.setSessionCheckError,
  );

  useEffect(() => {
    const clearSessionData = () => queryClient.clear();
    window.addEventListener(browserSessionResetEvent, clearSessionData);
    return () => {
      window.removeEventListener(browserSessionResetEvent, clearSessionData);
    };
  }, [queryClient]);

  useEffect(() => {
    try {
      return subscribeBrowserSessionMutations(() => {
        noteExternalBrowserSessionMutation();
        useAuthStore.getState().beginSessionCheck();
        queryClient.clear();
      });
    } catch (error) {
      setSessionCheckError(
        error instanceof Error ? error.message : 'Browser session coordination is unavailable.',
      );
      return undefined;
    }
  }, [queryClient, setSessionCheckError]);

  useEffect(() => {
    if (sessionStatus !== 'checking' || sessionCheckError) return;

    let disposed = false;
    void restoreBrowserSession().catch((error) => {
      if (disposed) return;
      if (isRefreshSessionRejected(error)) {
        endRejectedBrowserSession();
        return;
      }
      setSessionCheckError(
        error instanceof BrowserSessionLockUnavailableError
          ? error.message
          : SESSION_UNAVAILABLE_MESSAGE,
      );
    });

    return () => {
      disposed = true;
    };
  }, [sessionCheckError, sessionStatus, setSessionCheckError]);

  const retrySession = useCallback(async () => {
    try {
      await refreshAuthSession();
    } catch (error) {
      if (isRefreshSessionRejected(error)) {
        endRejectedBrowserSession();
      } else if (useAuthStore.getState().sessionStatus === 'signed-in') {
        markBrowserSessionUnavailable(
          error instanceof BrowserSessionLockUnavailableError
            ? error.message
            : SESSION_UNAVAILABLE_MESSAGE,
        );
      } else if (useAuthStore.getState().sessionStatus === 'checking') {
        setSessionCheckError(
          error instanceof BrowserSessionLockUnavailableError
            ? error.message
            : SESSION_UNAVAILABLE_MESSAGE,
        );
      }
    }
  }, [setSessionCheckError]);

  useEffect(() => {
    if (sessionCheckError !== SESSION_UNAVAILABLE_MESSAGE) return;

    const retryTimer = window.setInterval(
      () => void retrySession(),
      config.authSessionRetryMs,
    );
    const retryWhenOnline = () => void retrySession();
    window.addEventListener('online', retryWhenOnline);
    return () => {
      window.clearInterval(retryTimer);
      window.removeEventListener('online', retryWhenOnline);
    };
  }, [retrySession, sessionCheckError]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const expiresAt = readAccessTokenExpiry(token);
    if (expiresAt === null) return;

    let disposed = false;
    let refreshStarted = false;

    const refreshExpiredSession = async (force = false) => {
      if (disposed || refreshStarted || (!force && Date.now() < expiresAt)) return;
      refreshStarted = true;
      try {
        await refreshAuthSession();
      } catch (error) {
        refreshStarted = false;
        if (!disposed && isRefreshSessionRejected(error)) {
          endRejectedBrowserSession();
        } else if (!disposed) {
          markBrowserSessionUnavailable(
            error instanceof BrowserSessionLockUnavailableError
              ? error.message
              : SESSION_UNAVAILABLE_MESSAGE,
          );
        }
      }
    };

    const refreshDelay = Math.max(0, expiresAt - Date.now());
    const refreshTimer = window.setTimeout(
      () => void refreshExpiredSession(),
      refreshDelay,
    );
    const refreshAfterBrowserResume = () => {
      if (document.visibilityState === 'visible') {
        void refreshExpiredSession();
      }
    };

    document.addEventListener('visibilitychange', refreshAfterBrowserResume);
    window.addEventListener('focus', refreshAfterBrowserResume);
    const verifyAfterNetworkChange = () => void refreshExpiredSession(true);
    window.addEventListener('online', verifyAfterNetworkChange);

    return () => {
      disposed = true;
      window.clearTimeout(refreshTimer);
      document.removeEventListener('visibilitychange', refreshAfterBrowserResume);
      window.removeEventListener('focus', refreshAfterBrowserResume);
      window.removeEventListener('online', verifyAfterNetworkChange);
    };
  }, [isAuthenticated, token]);

  if (sessionStatus === 'checking' && sessionCheckError) {
    return (
      <Modal
        isOpen
        onClose={ignoreDismiss}
        ariaLabel="Session check failed"
        size="sm"
        showCloseButton={false}
        closeOnBackdrop={false}
        footer={<Button onClick={() => void retrySession()}>Retry now</Button>}
      >
        <div role="alert">
          <h2 className="text-section-title font-semibold text-ink-strong">
            Session check failed
          </h2>
          <p className="mt-2 text-body text-ink-muted">{sessionCheckError}</p>
          {sessionCheckError === SESSION_UNAVAILABLE_MESSAGE && (
            <p className="mt-2 text-body text-ink-muted">Reconnecting automatically.</p>
          )}
        </div>
      </Modal>
    );
  }

  if (sessionStatus === 'signed-in' && sessionCheckError) {
    return (
      <div
        className="fixed inset-x-0 top-0 z-[100] border-b border-status-unavailable-border bg-status-unavailable-surface px-4 py-3 text-status-unavailable shadow-panel"
        role="alert"
      >
        <div className="mx-auto flex max-w-screen-xl flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
          <strong className="font-semibold">Connection interrupted</strong>
          <span>Previously loaded information remains visible. Changes may fail until the connection returns.</span>
          <button
            type="button"
            className="rounded-control border border-status-unavailable-border bg-surface px-3 py-1 font-semibold text-status-unavailable hover:bg-status-unavailable-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={() => void retrySession()}
          >
            Retry now
          </button>
        </div>
      </div>
    );
  }

  return null;
}
